import type {
  Notification,
  NotificationPreferenceMatrix,
  PutNotificationPreferences,
  ReadAllResponse,
} from '@servicetim/shared';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { portalPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery } from './paging';

/*
 * In-app notification inbox (`/v1/notifications`, S-08-01) and the caller's channel
 * preferences (`/v1/me/notification-preferences`, S-08-03). Session-based; shared by the admin
 * bell/inbox and the portal inbox.
 */

interface NotificationPage {
  items: Notification[];
  nextCursor: string | null;
  unreadCount: number;
}

export interface NotificationListOptions {
  unreadOnly?: boolean;
  pageSize?: number;
  enabled?: boolean;
}

/**
 * `GET /v1/notifications?unreadOnly=&pageSize=&cursor=`. A hand-rolled infinite query (not
 * `usePagedList`) because every page also carries `unreadCount` for the caller across all
 * pages — the bell badge reads it off the first loaded page.
 */
export function useNotifications(options: NotificationListOptions = {}) {
  const { unreadOnly, pageSize = DEFAULT_PAGE_SIZE, enabled } = options;
  const { request, keys } = useApiContext();
  const query = useInfiniteQuery({
    queryKey: keys.notifications.list({ unreadOnly, pageSize }),
    queryFn: ({ pageParam, signal }: { pageParam: string | undefined; signal: AbortSignal }) =>
      request<NotificationPage>('GET', portalPath('/notifications'), {
        query: { unreadOnly, ...pageQuery(pageParam, pageSize) },
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });
  const items = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data]);
  const unreadCount = query.data?.pages[0]?.unreadCount ?? 0;
  return {
    ...query,
    items,
    unreadCount,
    hasMore: query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}

/** Unread badge count alone (bell icon in the shell header), without paging through the inbox. */
export function useUnreadNotificationCount(enabled = true) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.notifications.list({ unreadOnly: true, pageSize: 1 }),
    queryFn: ({ signal }) =>
      request<NotificationPage>('GET', portalPath('/notifications'), {
        query: { unreadOnly: true, pageSize: 1 },
        signal,
      }),
    select: (page) => page.unreadCount,
    enabled,
    // A missed increment/decrement self-heals quickly; no need to poll continuously (rule 8).
    staleTime: 30_000,
  });
}

/** `POST /v1/notifications/{id}/read` */
export function useMarkNotificationRead() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<void>('POST', portalPath(`/notifications/${id}/read`)),
    onSuccess: () => invalidate(keys.notifications.all),
  });
}

/** `POST /v1/notifications/read-all` */
export function useMarkAllNotificationsRead() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: () => request<ReadAllResponse>('POST', portalPath('/notifications/read-all')),
    onSuccess: () => invalidate(keys.notifications.all),
  });
}

/** `GET /v1/me/notification-preferences`: the full matrix, defaults merged in server-side. */
export function useNotificationPreferences() {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.notifications.preferences,
    queryFn: ({ signal }) =>
      request<NotificationPreferenceMatrix>('GET', portalPath('/me/notification-preferences'), {
        signal,
      }),
  });
}

/** `PUT /v1/me/notification-preferences`: a sparse patch, merged and returned in full. */
export function useUpdateNotificationPreferences() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (patch: PutNotificationPreferences) =>
      request<NotificationPreferenceMatrix>('PUT', portalPath('/me/notification-preferences'), {
        body: patch,
      }),
    onSuccess: () => invalidate(keys.notifications.preferences),
  });
}
